import React, { RefObject } from 'react';
import 'twin.macro';
import Button from 'components/Buttons/Button';
import { getFicheDePrep } from 'queries/fdp/useFicheDePrep';
import {
  BriefcaseIcon as Briefcase,
  DocumentTextIcon as DocumentText,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { AttachmentButton } from 'containers/CahierJournal/EventModal/Sidebar/AttachmentButton';
import { nanoid } from 'nanoid';
import { useSelectFicheDePrep } from 'containers/CahierJournal/EventModal/ModalBody/Classic/LinkedFichesDePreps/useSelectFicheDePrep';
import {
  useOpenFicheDePrepDrawer,
  useSetOnSelectFicheDePrep,
} from 'containers/CahierJournal/LinkFichesDePrepDrawer/atoms';
import { useField } from 'react-final-form';
import { getSequence } from 'queries/sequences/useSequence';
import { useTranslation } from 'react-i18next';
import { useOpenProgItemDrawer } from 'containers/CahierJournal/LinkProgItemDrawer/atoms';
import { BoFields } from 'types/bo';
import { Menu } from 'components/Menu';
import { AtelierMenuItems } from 'containers/CahierJournal/EventModal/AtelierMenuItems';
import { getStandaloneFicheDePrep } from 'queries/fdp/useStandaloneFicheDePrep';
import { ProgressionIcon } from 'components/Icons/icons/ProgressionIcon';

interface Props {
  ateliersRef?: RefObject<HTMLDivElement>;
  materialInputRef?: RefObject<HTMLInputElement>;
  attachmentInputRef?: RefObject<HTMLInputElement>;
  cycle: string;
  onAddToSeanceCallback?: () => void;
}

export function AddToSeanceButtons(props: Props) {
  const {
    materialInputRef,
    attachmentInputRef,
    ateliersRef,
    cycle,
    onAddToSeanceCallback,
  } = props;
  const materialsField = useField('materials');
  const boField = useField<BoFields>('bo');
  const tagField = useField('tag');
  const startField = useField<Date>('start');
  const onSelectFicheDePrep = useSelectFicheDePrep();
  const openDrawer = useOpenFicheDePrepDrawer();
  const openProgItemDrawer = useOpenProgItemDrawer();
  const setFicheDePrepPayload = useSetOnSelectFicheDePrep();
  const { t } = useTranslation(['common', 'cahierJournal']);

  return (
    <>
      <Button
        onClick={() => {
          openProgItemDrawer({
            isOpen: true,
            matiereName:
              boField.input.value?.matiere[0]?.value ||
              tagField.input.value?.title,
            domaineName: boField.input.value?.domaine[0]?.value,
            date: startField.input.value,
          });
        }}
        icon={ProgressionIcon as any}
        small
        alignText="left"
      >
        {t('cahierJournal:progressionElement')}
      </Button>
      <Button
        onClick={() => {
          if (setFicheDePrepPayload) {
            setFicheDePrepPayload(
              ({ ficheDePrepId, sequenceId, doNotPrefill }) => {
                if (sequenceId) {
                  return getFicheDePrep(sequenceId, ficheDePrepId).then(
                    ficheDePrep => {
                      if (!doNotPrefill) {
                        getSequence(sequenceId).then(sequence =>
                          onSelectFicheDePrep(ficheDePrep, sequence.title),
                        );
                      }
                      return ficheDePrep;
                    },
                  );
                }
                return getStandaloneFicheDePrep(ficheDePrepId).then(
                  ficheDePrep => {
                    if (!doNotPrefill) {
                      onSelectFicheDePrep(ficheDePrep, '');
                    }
                    return ficheDePrep;
                  },
                );
              },
            );
          }
          openDrawer();
        }}
        icon={DocumentText}
        alignText="left"
        small
      >
        {t('cahierJournal:seance.modal.ficheDePrep')}
      </Button>

      <Menu fill placement="bottom-end" portal>
        <Menu.Button alignText="left" icon={PlusIcon} small>
          {t('cahierJournal:seance.modal.atelier')}
        </Menu.Button>
        <Menu.Items size="auto">
          <AtelierMenuItems
            cycle={cycle}
            ateliersRef={ateliersRef}
            onAddToSeanceCallback={onAddToSeanceCallback}
          />
        </Menu.Items>
      </Menu>
      <AttachmentButton
        formFieldName="attachments"
        inputRef={attachmentInputRef}
        onAddToSeanceCallback={onAddToSeanceCallback}
      />
      <Button
        onClick={() => {
          if ((materialsField?.input?.value || []).length === 0) {
            materialsField.input.onChange([{ value: '', id: nanoid(5) }]);
          }
          setTimeout(() => {
            materialInputRef?.current?.focus();
          }, 1);
          onAddToSeanceCallback?.();
        }}
        icon={Briefcase}
        alignText="left"
        small
      >
        {t('cahierJournal:seance.material')}
      </Button>
    </>
  );
}
